import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router'
import { PremiumGate } from '../../platform/access/PremiumGate'
import { useEntitlement } from '../../platform/access/useEntitlement'
import { localToday, type LocalDate } from '../../platform/localDate'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { Panel } from '../../platform/primitives/Panel'
import { useAccounts } from '../accounts/useAccounts'
import { BUDGET_CURRENCY, BUDGET_PATH, budgetDetailPath, budgetForCategory, isOver, shownPct, useBudgets } from '../budget/useBudgets'
import { useCategories } from '../categories/useCategories'
import { NotFound } from '../errors/NotFound'
import { BrandSuggestion, useBrandGroups } from '../merchants/catalogue'
import { contentRegion, focusFirstAlive } from '../../platform/a11y/focusFirstAlive'
import { useMerchant } from '../merchants/useMerchants'
import { useCategoryQuickPick } from '../transactions/useCategoryQuickPick'
import { useMerchantQuickPick } from '../transactions/useMerchantQuickPick'
import { useScopedTransactions, type ScopedTxnQuery } from '../transactions/useScopedTransactions'
import { emitDetailOpened } from './cashflowAnalytics'
import { UNCATEGORISED } from './cashflowCategories'
import { CashflowDetailScreen, type DetailBudget, type DetailName, type DetailShare } from './CashflowDetailScreen'
import { clampPeriod, lowerBound, periodWindow, unitStart, readView, writeView, type CashflowView } from './cashflowPeriod'
import { readScope, readSide, scopeQuery, type DetailScope, type Side } from './cashflowScope'
import { ChartPreview } from './LockedPreview'
import { useCashflowHistory, useCashflowPeriod } from './useCashflow'
import { useViewChange } from './useViewChange'
import { NO_MERCHANT } from './WhereItWent'
import { nameGroups } from './brandGroups'
import { largestSameDescription } from './sameDescription'
import { SameDescriptionPrompt, useSameDescriptionPick } from './SameDescriptionPrompt'
import { useMerchantForRows } from './useMerchantForRows'
import './cashflow.css'

/** What the last answered scoped period call said about the bounds. */
type Bounds = { earliest: LocalDate | null; today: LocalDate }

/** `/cashflow?<view>`, with no `?` when the view is the default. */
function overviewHref(view: CashflowView, asOf: LocalDate): string {
  const q = writeView(view, asOf).toString()
  return q === '' ? '/cashflow' : `/cashflow?${q}`
}

/**
 * The list's filter, renamed from `scopeQuery`'s params: that table is the ONE place a scope
 * becomes a filter, for the report and the list alike. Nothing here decides which param a kind sends.
 */
function listFilter(
  s: DetailScope,
): Pick<ScopedTxnQuery, 'categoryId' | 'groupId' | 'merchantId' | 'merchantName' | 'uncategorized' | 'noMerchant'> {
  const q = scopeQuery(s)
  return {
    ...(q.category_id === undefined ? {} : { categoryId: q.category_id }),
    ...(q.group_id === undefined ? {} : { groupId: q.group_id }),
    ...(q.merchant_id === undefined ? {} : { merchantId: q.merchant_id }),
    ...(q.merchant_name === undefined ? {} : { merchantName: q.merchant_name }),
    ...(q.uncategorized === undefined ? {} : { uncategorized: q.uncategorized }),
    ...(q.no_merchant === undefined ? {} : { noMerchant: q.no_merchant }),
  }
}

/**
 * `/cashflow/:scope/:id?` — one row of the overview opened on its own.
 *
 * An unknown scope word is the app's own NotFound; a free user gets the
 * `PremiumGate` locked state and nothing is fetched (both decided before any
 * hook that fetches is mounted). Otherwise `CashflowDetailLoaded` owns the
 * URL, the calls and the analytics, and the screen draws.
 */
export function CashflowDetail() {
  const params = useParams()
  const [search] = useSearchParams()
  const entitled = useEntitlement('cashflow_details')
  const scope = useMemo(() => readScope({ scope: params.scope, id: params.id }), [params.scope, params.id])
  const side = readSide(search)
  if (scope === null) return <NotFound />
  if (!entitled) {
    const today = localToday()
    return (
      <div className="cf-page cf-detail">
        {/* The way back still works locked: Cashflow at the period the link carried. */}
        <nav aria-label="Cashflow breadcrumb" className="cf-detail__crumb">
          <InlineLink to={overviewHref(readView(search, today), today)}>Cashflow</InlineLink>
        </nav>
        <Panel className="cf-panel">
          {/* No `panelKind`: the whole page is locked, not one panel of it. */}
          <PremiumGate entitlement="cashflow_details" preview={<ChartPreview />}>
            {null}
          </PremiumGate>
        </Panel>
      </div>
    )
  }
  // Keyed by the row, so another row's page never inherits this one's bounds, page or analytics.
  return <CashflowDetailLoaded key={JSON.stringify([scope, side])} scope={scope} side={side} />
}

function CashflowDetailLoaded({ scope, side }: { scope: DetailScope; side: Side }) {
  const [params, setParams] = useSearchParams()
  // One day for the whole mount: a render across midnight must not re-key the queries.
  const today = useMemo(() => localToday(), [])
  const tz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, [])

  // The same grammar and clamp as `/cashflow`; the floor is the SCOPED call's earliest, where this row's data starts.
  const [bounds, setBounds] = useState<Bounds | null>(null)
  const asOf = bounds?.today ?? today
  const earliest = bounds === null ? undefined : bounds.earliest
  const asked = readView(params, asOf)
  const granularity = asked.granularity
  const lower = lowerBound(earliest ?? null, granularity, asOf)
  const clamped = clampPeriod(asked.period, granularity, lower, asOf)
  const view = useMemo<CashflowView>(() => ({ granularity, period: clamped }), [granularity, clamped])

  const period = useCashflowPeriod({ view, today, tz, details: true, scope })
  const answered = period.state.status === 'success' ? period.state.data.window : null
  if (answered !== null && (bounds === null || bounds.earliest !== answered.earliest || bounds.today !== answered.today)) {
    // Adjusting state during render (not an effect), as `Cashflow` does.
    setBounds({ earliest: answered.earliest, today: answered.today })
  }

  const raw = params.get('period')
  useEffect(() => {
    const canonical = writeView({ granularity, period: clamped }, asOf)
    if (side === 'in') canonical.set('side', 'in')
    // REPLACE, not push: the non-canonical URL was never a place to go Back to.
    if (raw !== canonical.get('period')) setParams(canonical, { replace: true })
  }, [raw, clamped, granularity, setParams, asOf, side])

  const history = useCashflowHistory({ granularity, today: asOf, tz, enabled: true, earliest, scope })
  const periodNeverAnswered = bounds === null && period.state.status === 'error'

  // The share is the overview's own figure for this row, from the overview's own (normally cached) query.
  const wantsShare = side === 'out' && (scope.kind === 'category' || scope.kind === 'group')
  const overview = useCashflowPeriod({ view, today, tz, details: true, enabled: wantsShare })
  const overviewData = overview.state.status === 'success' || overview.state.status === 'stale' ? overview.state.data : null
  const shareLoading = wantsShare && overview.state.status === 'loading'
  const share = useMemo<DetailShare | 'loading' | null>(() => {
    if (shareLoading) return 'loading'
    if (!wantsShare || overviewData === null || overviewData.breakdown === null) return null
    const b = overviewData.breakdown
    const row =
      scope.kind === 'category'
        ? b.categories.find((c) => c.categoryId === scope.id)
        : scope.kind === 'group'
          ? b.groups.find((gr) => gr.groupId === scope.id)
          : undefined
    // Absent, or flagged (a negative amount): no tile.
    if (row === undefined || row.amountCents < 0) return null
    return { pct: row.sharePct, expenseCents: overviewData.totals.expenseCents, currency: overviewData.currency }
  }, [wantsShare, shareLoading, overviewData, scope])

  // A budget's progress is the server's, for the CURRENT month: it answers for a category's spending
  // page opened on this month and no other (not a past month, a quarter, or the income side).
  const wantsBudget = scope.kind === 'category' && side === 'out' && view.granularity === 'month' && view.period === unitStart(asOf, 'month')
  const budgets = useBudgets({ enabled: wantsBudget })
  const budget = useMemo<DetailBudget | 'loading' | null>(() => {
    if (!wantsBudget || scope.kind !== 'category') return null
    if (budgets.status === 'loading') return 'loading'
    if (budgets.status === 'error') return { kind: 'error', retry: budgets.recovery?.onAct }
    // No budgets at all is the same answer as none for this category.
    if (budgets.status === 'empty') return { kind: 'unset', href: BUDGET_PATH }
    const found = budgetForCategory(budgets.data, scope.id)
    if (found.kind === 'none') return { kind: 'unset', href: BUDGET_PATH }
    if (found.kind === 'other') return { kind: 'not_monthly', href: found.envelope === null ? BUDGET_PATH : budgetDetailPath(found.envelope.id) }
    const e = found.envelope
    // The server's figures as they are: over shows spent against the limit and the Budget screen's own percentage.
    return isOver(e)
      ? { kind: 'over', spentCents: e.progress.spentCents, limitCents: e.amountCents, pct: shownPct(e), currency: BUDGET_CURRENCY }
      : { kind: 'left', remainingCents: e.progress.remainingCents, currency: BUDGET_CURRENCY }
  }, [wantsBudget, scope, budgets])

  // The list pages within one view; a new period starts at page 1 again.
  const viewKey = `${view.granularity}:${view.period}`
  const [paging, setPaging] = useState({ viewKey, page: 1 })
  const page = paging.viewKey === viewKey ? paging.page : 1
  const { start, end } = periodWindow(view.period, view.granularity, today)
  const transactions = useScopedTransactions({ start, end, kind: side === 'in' ? 'income' : 'expense', page, ...listFilter(scope) })

  const categoriesState = useCategories()
  const categoryRows = categoriesState.status === 'success' ? categoriesState.data : null
  const categoryNames = useMemo(() => new Map((categoryRows ?? []).map((c) => [c.id, c.name] as const)), [categoryRows])
  const accountsState = useAccounts()
  const accounts = useMemo(
    () =>
      new Map(
        accountsState.status === 'success' || accountsState.status === 'stale' ? accountsState.data.map((a) => [a.id, a] as const) : [],
      ),
    [accountsState],
  )
  const merchant = useMerchant(scope.kind === 'merchant' ? scope.id : undefined)

  const name = useMemo<DetailName>(() => {
    switch (scope.kind) {
      case 'uncategorised':
        return { status: 'ready', text: UNCATEGORISED, icon: null }
      case 'no_merchant':
        return { status: 'ready', text: NO_MERCHANT, icon: null }
      case 'payer':
        return { status: 'ready', text: scope.name, icon: null }
      case 'merchant':
        if (merchant.status === 'success' || merchant.status === 'stale') return { status: 'ready', text: merchant.data.name, icon: null }
        // A merchant that will not load still has a page: its figures are the scoped call's.
        // A failed name says so and offers its own retry; the figures are the scoped call's and still load.
        if (merchant.status === 'error') return { status: 'error', retry: merchant.recovery?.onAct }
        if (merchant.status === 'empty') return { status: 'ready', text: 'Merchant', icon: null }
        return { status: 'loading' }
      default: {
        if (categoriesState.status === 'error') return { status: 'error', retry: categoriesState.recovery?.onAct }
        // A list that loaded without this id names it as the overview does (`categoryName`'s "Other category").
        if (categoryRows === null) return categoriesState.status === 'loading' ? { status: 'loading' } : { status: 'ready', text: 'Other category', icon: null }
        const c = categoryRows.find((x) => x.id === scope.id)
        return c === undefined ? { status: 'ready', text: 'Other category', icon: null } : { status: 'ready', text: c.name, icon: c }
      }
    }
  }, [scope, merchant, categoryRows, categoriesState])

  const opened = useRef(false)
  useEffect(() => {
    if (opened.current) return
    opened.current = true
    emitDetailOpened(scope.kind, side)
  }, [scope.kind, side])

  // A push, so Back restores the period before; a keyboard run through the chart replaces after its first key.
  const write = useCallback(
    (next: CashflowView, replace: boolean) => {
      const q = writeView(next, asOf)
      if (side === 'in') q.set('side', 'in')
      setParams(q, { replace })
    },
    [setParams, asOf, side],
  )
  const locationKey = useLocation().key
  const onViewChange = useViewChange(view.granularity, write, locationKey)

  const onPage = useCallback((n: number) => setPaging({ viewKey, page: n }), [viewKey])
  const pick = useCategoryQuickPick()
  const merchantPick = useMerchantQuickPick()
  const groupPick = useSameDescriptionPick()
  // The No merchant page offers to set the largest group of rows sharing a bank description at once.
  const group = scope.kind === 'no_merchant' && transactions.status === 'success' ? largestSameDescription(transactions.data.rows) : null
  // "Looks like <brand>: add as a merchant?" once per BRAND (#494): the groups of rows sharing a cleaned
  // name are matched, and every group that looks like one brand goes under its one suggestion. Add sets the
  // new merchant on all their rows, as "Set all N at once?" does, with one Undo.
  const brandRows = useMerchantForRows()
  const nameGroupsHere = scope.kind === 'no_merchant' && transactions.status === 'success' ? nameGroups(transactions.data.rows) : []
  const byBrand = useBrandGroups(nameGroupsHere, (g) => g.name)
  // Each suggestion's Add, by brand: where focus goes when a suggestion leaves (Not now, Add) with it inside.
  const addButtons = useRef(new Map<string, HTMLButtonElement>())
  const focusAfter = (brandId: string) => {
    const order = byBrand.map((b) => b.brand.id)
    const at = order.indexOf(brandId)
    const rest = [...order.slice(at + 1), ...order.slice(0, Math.max(at, 0))].filter((id) => id !== brandId)
    focusFirstAlive(...rest.map((id) => addButtons.current.get(id)), contentRegion())
  }
  const suggestions = byBrand.map(({ brand, name: matched, items }) => {
    const rows = items.flatMap((g) => g.rows)
    return (
      <BrandSuggestion
        key={brand.id}
        name={matched}
        txnIds={rows.map((r) => r.id)}
        offerCategory={rows.every((r) => r.categoryId === null)}
        onAdded={(m, extras) => void brandRows.set(rows, m, extras)}
        returnFocus={() => focusAfter(brand.id)}
        addRef={(el) => {
          if (el === null) addButtons.current.delete(brand.id)
          else addButtons.current.set(brand.id, el)
        }}
        panelClassName="cf-panel"
      />
    )
  })

  return (
    <>
      <CashflowDetailScreen
        scope={scope}
        side={side}
        name={name}
        view={view}
        today={asOf}
        earliest={earliest}
        period={period}
        history={earliest !== null && !periodNeverAnswered ? history : null}
        share={share}
        budget={budget}
        wantsBudget={wantsBudget}
        transactions={transactions}
        accounts={accounts}
        categories={categoryNames}
        categoriesStatus={categoriesState.status}
        crumbHref={overviewHref(view, asOf)}
        onViewChange={onViewChange}
        onPage={onPage}
        onCategoryClick={pick.onCategoryClick}
        onMerchantClick={merchantPick.onMerchantClick}
        prompt={
          group === null && suggestions.length === 0 ? null : (
            <>
              {group !== null && <SameDescriptionPrompt group={group} onChoose={groupPick.onChoose} busy={groupPick.busy} />}
              {suggestions}
            </>
          )
        }
      />
      {pick.dialog}
      {merchantPick.dialog}
      {groupPick.dialog}
    </>
  )
}
